import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  message,
  Modal,
  Image,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Typography,
  Tabs,
  Descriptions,
  Spin
} from 'antd';
import {
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  UserOutlined,
  BookOutlined,
  DollarOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { adminAPI } from '../../services/api';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { confirm } = Modal;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface Order {
  registration_id: number;
  registration_time: string;
  registration_status: string;
  payment_status: string;
  payment_time: string;
  username: string;
  student_name: string;
  student_phone: string;
  course_name: string;
  course_price: number;
  teacher_name: string;
  start_date: string;
  screenshot_id?: number;
  screenshot_url?: string;
  screenshot_status?: string;
  order_number?: string;
  payment_amount?: number;
  screenshot_payment_time?: string;
  merchant_order_number?: string;
  admin_notes?: string;
}

const OrderManagement: React.FC = () => {
  const [todayOrders, setTodayOrders] = useState<Order[]>([]);
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [todayLoading, setTodayLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0
  });
  const [filters, setFilters] = useState({
    status: '',
    course_id: '',
    date: '',
    search: ''
  });
  const [todayStats, setTodayStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    paid: 0,
    revenue: 0
  });
  const [screenshotModal, setScreenshotModal] = useState<{
    visible: boolean;
    data: Order | null;
  }>({ visible: false, data: null });
  const [reviewModal, setReviewModal] = useState<{
    visible: boolean;
    data: Order | null;
    notes: string;
  }>({ visible: false, data: null, notes: '' });

  const navigate = useNavigate();

  useEffect(() => {
    fetchTodayOrders();
    fetchAllOrders();
  }, []);

  // 当过滤器变化时自动加载
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAllOrders(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const fetchTodayOrders = async () => {
    setTodayLoading(true);
    try {
      const data = await adminAPI.getTodayOrders();
      setTodayOrders(data);
      
      // 计算今日统计
      const stats = {
        total: data.length,
        pending: data.filter(o => o.registration_status === 'pending').length,
        confirmed: data.filter(o => o.registration_status === 'confirmed').length,
        paid: data.filter(o => o.payment_status === 'paid').length,
        revenue: data
          .filter(o => o.payment_status === 'paid')
          .reduce((sum, o) => sum + o.course_price, 0)
      };
      setTodayStats(stats);
    } catch (error) {
      message.error('获取今日订单失败');
    } finally {
      setTodayLoading(false);
    }
  };

  const fetchAllOrders = async (page = 1, limit = 20) => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit
      };
      
      if (filters.status) params.status = filters.status;
      if (filters.date) params.date = filters.date;
      if (filters.search) params.search = filters.search;
      
      const data = await adminAPI.getAllOrders(params);
      setAllOrders(data.orders);
      setPagination(data.pagination);
    } catch (error) {
      message.error('获取订单列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理搜索变化
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleStatusChange = (value: string) => {
    setFilters(prev => ({ ...prev, status: value }));
  };

  const handleDateChange = (date: any) => {
    setFilters(prev => ({ ...prev, date: date?.format('YYYY-MM-DD') || '' }));
  };

  const handleStatusUpdate = async (orderId: number, status: string, notes?: string) => {
    try {
      await adminAPI.updateOrderStatus(orderId, status, notes);
      message.success('订单状态更新成功');
      fetchTodayOrders();
      fetchAllOrders(pagination.currentPage);
    } catch (error) {
      message.error('更新订单状态失败');
    }
  };

  const handleScreenshotReview = async (screenshotId: number, status: string, notes?: string) => {
    try {
      await adminAPI.reviewScreenshot(screenshotId, status, notes);
      message.success('截图审核完成');
      fetchTodayOrders();
      fetchAllOrders(pagination.currentPage);
    } catch (error) {
      message.error('审核失败');
    }
  };

  const showReviewModal = (order: Order) => {
    setReviewModal({ visible: true, data: order, notes: '' });
  };

  const handleReviewSubmit = () => {
    if (!reviewModal.data) return;
    
    if (reviewModal.data.screenshot_id) {
      handleScreenshotReview(
        reviewModal.data.screenshot_id,
        reviewModal.data.screenshot_status === 'pending' ? 'approved' : 'pending',
        reviewModal.notes
      );
    } else {
      handleStatusUpdate(reviewModal.data.registration_id, 'confirmed', reviewModal.notes);
    }
    
    setReviewModal({ visible: false, data: null, notes: '' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
      case 'approved':
        return 'green';
      case 'pending':
        return 'orange';
      case 'cancelled':
      case 'rejected':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      confirmed: '已确认',
      pending: '待审核',
      cancelled: '已取消',
      approved: '已通过',
      rejected: '已拒绝'
    };
    return statusMap[status] || status;
  };

  const columns = [
    {
      title: '学生信息',
      key: 'student',
      render: (_: any, record: Order) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.student_name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.username}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {record.student_phone}
          </div>
        </div>
      )
    },
    {
      title: '课程信息',
      key: 'course',
      render: (_: any, record: Order) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.course_name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            讲师：{record.teacher_name}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            开课：{dayjs(record.start_date).format('MM-DD')}
          </div>
        </div>
      )
    },
    {
      title: '金额',
      dataIndex: 'course_price',
      render: (price: number) => `¥${price}`,
      width: 80
    },
    {
      title: '报名时间',
      dataIndex: 'registration_time',
      render: (time: string) => dayjs(time).format('MM-DD HH:mm'),
      width: 120
    },
    {
      title: '订单号',
      dataIndex: 'order_number',
      render: (orderNumber: string, record: Order) => (
        <div>
          <div>{orderNumber || '-'}</div>
          {record.merchant_order_number && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              商户: {record.merchant_order_number}
            </div>
          )}
        </div>
      ),
      width: 150
    },
    {
      title: '支付状态',
      key: 'payment_status',
      render: (_: any, record: Order) => (
        <Space>
          <Tag color={getStatusColor(record.payment_status)}>
            {record.payment_status === 'paid' ? '已支付' : '待支付'}
          </Tag>
          {record.screenshot_status && (
            <Tag color={getStatusColor(record.screenshot_status)}>
              {getStatusText(record.screenshot_status)}
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Order) => (
        <Space>
          {record.screenshot_url && (
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setScreenshotModal({ visible: true, data: record })}
            >
              查看截图
            </Button>
          )}
          {record.screenshot_status === 'pending' ? (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => showReviewModal(record)}
            >
              审核
            </Button>
          ) : (
            <Button
              type="text"
              size="small"
              onClick={() => showReviewModal(record)}
            >
              详情
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>订单管理</Title>
      </div>

      <Tabs defaultActiveKey="today">
        <TabPane tab={`今日报名 (${todayOrders.length})`} key="today">
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="今日订单"
                  value={todayStats.total}
                  prefix={<BookOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="待审核"
                  value={todayStats.pending}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="已确认"
                  value={todayStats.confirmed}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="今日收入"
                  value={todayStats.revenue}
                  prefix={<DollarOutlined />}
                  precision={2}
                />
              </Card>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={todayOrders}
            loading={todayLoading}
            pagination={false}
            rowKey="registration_id"
            scroll={{ x: 800 }}
          />
        </TabPane>

        <TabPane tab="全部订单" key="all">
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={8}>
              <Input
                placeholder="搜索学生姓名或用户名"
                prefix={<SearchOutlined />}
                onChange={handleSearchChange}
              />
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Select
                placeholder="订单状态"
                style={{ width: '100%' }}
                allowClear
                onChange={handleStatusChange}
              >
                <Select.Option value="">全部</Select.Option>
                <Select.Option value="pending">待审核</Select.Option>
                <Select.Option value="confirmed">已确认</Select.Option>
                <Select.Option value="cancelled">已取消</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <DatePicker
                style={{ width: '100%' }}
                onChange={handleDateChange}
              />
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Button type="primary" onClick={() => fetchAllOrders(1)}>
                重置
              </Button>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={allOrders}
            loading={loading}
            pagination={{
              current: pagination.currentPage,
              total: pagination.totalRecords,
              pageSize: 20,
              onChange: (page) => fetchAllOrders(page)
            }}
            rowKey="registration_id"
            scroll={{ x: 800 }}
          />
        </TabPane>
      </Tabs>

      {/* 查看截图模态框 */}
      <Modal
        title="支付截图详情"
        open={screenshotModal.visible}
        onCancel={() => setScreenshotModal({ visible: false, data: null })}
        footer={[
          <Button key="close" onClick={() => setScreenshotModal({ visible: false, data: null })}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {screenshotModal.data && (
          <div>
            {screenshotModal.data.screenshot_url && (
              <Image
                src={screenshotModal.data.screenshot_url}
                alt="支付截图"
                style={{ width: '100%', marginBottom: 16 }}
              />
            )}
            <Descriptions column={2}>
              <Descriptions.Item label="学生姓名">
                {screenshotModal.data.student_name}
              </Descriptions.Item>
              <Descriptions.Item label="课程名称">
                {screenshotModal.data.course_name}
              </Descriptions.Item>
              <Descriptions.Item label="订单号">
                {screenshotModal.data.order_number || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="支付金额">
                ¥{screenshotModal.data.payment_amount}
              </Descriptions.Item>
              <Descriptions.Item label="支付时间">
                {screenshotModal.data.screenshot_payment_time ? 
                  dayjs(screenshotModal.data.screenshot_payment_time).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="商户订单号">
                {screenshotModal.data.merchant_order_number || '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 审核模态框 */}
      <Modal
        title="订单审核"
        open={reviewModal.visible}
        onCancel={() => setReviewModal({ visible: false, data: null, notes: '' })}
        onOk={handleReviewSubmit}
      >
        {reviewModal.data && (
          <div>
            <Descriptions column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="学生姓名">
                {reviewModal.data.student_name}
              </Descriptions.Item>
              <Descriptions.Item label="课程名称">
                {reviewModal.data.course_name}
              </Descriptions.Item>
              <Descriptions.Item label="支付金额">
                ¥{reviewModal.data.course_price}
              </Descriptions.Item>
            </Descriptions>
            <TextArea
              rows={4}
              placeholder="审核备注"
              value={reviewModal.notes}
              onChange={(e) => setReviewModal({ ...reviewModal, notes: e.target.value })}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrderManagement;